import {getTestProps} from '@benchling/js-test-utils';
import {round} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent, useState} from 'react';

import {
  calculateLength,
  calculateNumMismatches,
  calculatePairwiseIdentity,
} from 'analyses/alignment/RowStatisticsUtil';
import {NucleotideType} from 'biojs/BioConstants';
import {DNAAlignmentRowType, ProteinAlignmentKind} from 'constants/AlignmentConstants';
import AntibodyInfo from 'molbio/aa-sequences/alignments/AntibodyInfo';
import {shouldShowAntibodyTools} from 'molbio/antibody-engineering/visibilityHelpers';
import {BaseSliderProps, AlignmentTraceSlider} from 'molbio/maps/options/Sliders';
import {AlignmentRowVisibilityOptionsDropdown} from 'settings/visibility-options/DisplayOptions';
import {FeatureCounts, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import Badge, {BadgeColor} from 'ui-library/Badge';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import TextInput from 'ui-library/inputs/TextInput';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';
import {Optional} from 'util/Types';

function NameEditModal({
  originalName,
  onRequestClose,
  onRequestSubmit,
}: {
  originalName: string;
  onRequestClose: () => void;
  onRequestSubmit: (name: string) => void;
}): JSX.Element {
  const [name, setName] = useState(originalName);
  return (
    <Modal
      title="Edit Row Name"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(name);
      }}
      buttons={[
        {text: 'Save', submit: true},
        {text: 'Cancel', cancel: true},
      ]}
    >
      <TextInput
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setName(e.target.value);
        }}
        value={name}
      />
    </Modal>
  );
}

export interface AlignmentRowInfoProps {
  name: string;
  residues: string;
  templateResidues?: string;
  trimStart?: number;
  trimEnd?: number;
  alignmentType: ProteinAlignmentKind;
  nucleotideType?: NucleotideType;
  canEdit: boolean;
  isAaSequenceAlignment: boolean;
  isReferenceRow: boolean;
  visibilityOptions: VisibilityOptions;
  onNameChange?: (name: string) => void;
  reversed?: Optional<boolean>;
}

export function AlignmentRowInfo(props: AlignmentRowInfoProps): JSX.Element {
  const start = props.trimStart ?? 0;
  const end = props.trimEnd ?? props.residues.length;

  let directionBadge = null;
  if (props.reversed === false) {
    directionBadge = (
      <Badge
        color={BadgeColor.BLUE}
        className="u-tiny-spacing-right"
        dataTestAttrs={{component: 'AlignmentRowInfo', element: 'directionBadge', key: BadgeColor.BLUE}}
      >
        FWD
      </Badge>
    );
  } else if (props.reversed === true) {
    directionBadge = (
      <Badge
        color={BadgeColor.RED}
        className="u-tiny-spacing-right"
        dataTestAttrs={{component: 'AlignmentRowInfo', element: 'directionBadge', key: BadgeColor.RED}}
      >
        REV
      </Badge>
    );
  }

  const showRowStats =
    props.alignmentType === ProteinAlignmentKind.TEMPLATE &&
    !props.isReferenceRow &&
    props.visibilityOptions.getOption(vo.ALIGNMENT_ROW_STATS.name)!.get('visible');

  const rowIcon =
    props.nucleotideType != null
      ? props.nucleotideType === NucleotideType.DNA
        ? FolderItemIcon.Sequence
        : FolderItemIcon.RNASequence
      : FolderItemIcon.Protein;

  return (
    <div className={css(styles.section)}>
      {props.isReferenceRow && <span className={css(styles.prefix)}>{capitalize(props.alignmentType)}</span>}
      <div className={css(styles.name)}>
        <span>
          <BenchlingIcon className={css(styles.icon, styles.nucleotideTypeIcon)} icon={rowIcon} />
          {directionBadge}
          {props.name}
        </span>
        {props.canEdit && (
          <BenchlingIcon
            className={css(styles.icon, styles.editIcon)}
            icon="edit"
            onClick={() => {
              legacyRunModal(NameEditModal, {originalName: props.name, onRequestSubmit: props.onNameChange});
            }}
          />
        )}
      </div>
      {props.isAaSequenceAlignment && shouldShowAntibodyTools(props.visibilityOptions) && (
        <AntibodyInfo residues={props.residues} visibilityOptions={props.visibilityOptions} />
      )}
      {showRowStats && (
        <>
          <div>Length: {calculateLength(props.residues, start, end)}</div>
          <div>Mismatches: {calculateNumMismatches(props.residues, props.templateResidues!, start, end)}</div>
          <div>
            Pairwise Identity:{' '}
            {round(calculatePairwiseIdentity(props.residues, props.templateResidues!, start, end) * 100, 2)}%
          </div>
        </>
      )}
    </div>
  );
}

export interface AlignmentRowInfoWrapperProps {
  alignmentRowType?: DNAAlignmentRowType | ProteinAlignmentKind;
  canEdit: boolean;
  reserveSpaceRight?: boolean;
  options: VisibilityOptions;
  featureCounts?: FeatureCounts;
  rowInfoProps: AlignmentRowInfoProps;
  onDeleteRow: () => void;
  sliderProps?: BaseSliderProps;
}

// TODO: there is some overlap in props between AlignmentRowInfoProps and AlignmentRowInfoWrapperProps and
// the components can probably be combined or at least organized nicer.
export default function AlignmentRowInfoWrapper({
  alignmentRowType,
  canEdit,
  reserveSpaceRight,
  onDeleteRow,
  options,
  featureCounts,
  rowInfoProps,
  sliderProps,
}: AlignmentRowInfoWrapperProps): JSX.Element {
  return (
    <>
      <AlignmentRowVisibilityOptionsDropdown
        options={options}
        featureCounts={featureCounts}
        dropLeft={false}
      />
      <div
        style={{height: '100%', width: reserveSpaceRight ? '75%' : '85%'}}
        {...getTestProps({component: 'AlignmentRowInfoWrapper', element: 'rowInfoContainer'})}
      >
        <AlignmentRowInfo {...rowInfoProps} />
      </div>
      {alignmentRowType === 'aligned' && canEdit && (
        <span style={{marginLeft: 'auto'}} className="times pull-right" onClick={onDeleteRow}>
          &times;
        </span>
      )}
      {sliderProps && (
        <div className="slider-container">
          <div style={{height: '100%'}} className="slider">
            <AlignmentTraceSlider {...sliderProps} />
          </div>
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  section: {
    overflowY: 'auto',
    height: '100%',
    padding: '0px 2px 2px 2px',
    wordWrap: 'break-word',
    fontSize: '11px',
    '::-webkit-scrollbar': {
      width: '8px',
    },
    '::-webkit-scrollbar-track': {
      background: '#f8fbfb',
      border: '1px solid #e8f0f2',
    },
    '::-webkit-scrollbar-thumb': {
      background: '#c9d8db',
    },
  },
  prefix: {
    fontWeight: 600,
  },
  name: {
    marginBottom: '2px',
  },
  icon: {
    display: 'inline',
  },
  editIcon: {
    cursor: 'pointer',
    marginLeft: '4px',
  },
  nucleotideTypeIcon: {
    marginRight: '4px',
  },
});
